/**
 * Resolve backend and Auth BFF origins.
 * Prefer VUE_APP_*_BASE_PATH (current frontend origin + path) over full *_URL.
 */

function trimTrailingSlash(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function normalizeBasePath(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return path.replace(/\/$/, '');
}

export function currentOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

/**
 * @param {string} basePathEnv
 * @param {string} fallbackUrlEnv
 * @param {string} [origin]
 * @returns {string}
 */
export function resolveServiceUrl(basePathEnv, fallbackUrlEnv, origin = currentOrigin()) {
  const basePath = normalizeBasePath(basePathEnv);
  if (basePath) {
    const resolvedOrigin = trimTrailingSlash(origin);
    return resolvedOrigin ? `${resolvedOrigin}${basePath}` : basePath;
  }
  return trimTrailingSlash(fallbackUrlEnv);
}

export function getApiUrl(origin = currentOrigin()) {
  return resolveServiceUrl(
    process.env.VUE_APP_API_BASE_PATH,
    process.env.VUE_APP_API_URL,
    origin,
  );
}

export function getAuthBffUrl(origin = currentOrigin()) {
  return resolveServiceUrl(
    process.env.VUE_APP_AUTH_BFF_BASE_PATH,
    process.env.VUE_APP_AUTH_BFF_URL,
    origin,
  );
}

export function isAuthBffConfigured(origin = currentOrigin()) {
  return Boolean(getAuthBffUrl(origin));
}

export function getFileUploadUrl(origin = currentOrigin()) {
  return resolveServiceUrl(
    process.env.VUE_APP_FILE_UPLOAD_PATH,
    process.env.VUE_APP_FILE_UPLOAD_API_URL,
    origin,
  );
}

/**
 * Combine a service base with a request path without duplicating the mount
 * (e.g. base `https://app/auth` + path `/auth/discover` → `https://app/auth/discover`).
 */
export function joinServiceUrl(baseUrl, path) {
  const rawPath = String(path || '');
  if (!rawPath) return trimTrailingSlash(baseUrl);
  if (/^https?:\/\//i.test(rawPath)) return rawPath;

  const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  const base = trimTrailingSlash(baseUrl);
  if (!base) return normalizedPath;

  try {
    const parsed = new URL(base, currentOrigin() || 'http://localhost');
    const basePath = parsed.pathname.replace(/\/$/, '');
    if (basePath && (normalizedPath === basePath || normalizedPath.startsWith(`${basePath}/`))) {
      return `${parsed.origin}${normalizedPath}`;
    }
    return `${parsed.origin}${basePath}${normalizedPath}`;
  } catch (_) {
    return `${base}${normalizedPath}`;
  }
}
